import { ColDef } from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { Box } from '@atgfr8/atg-ui-tailwind';
import { agGridCellStyle } from '@components/Grid/AgGridCellStyle';
import {
  columnsToolPanel,
  filtersToolPanel,
} from '@components/Grid/components/constants';
import { getRowGroupStyle } from '@components/Grid/utils';
import { Notification } from '@components/Notification';
import { useParams } from '@solidjs/router';
import {
  CustomerEditHistoryItem,
  fetchCustomerEditHistory,
} from '@store/customers/customerDetails';
import { dateComparator } from '@store/loadboard/utils';
import { atgLogo } from '@views/ltl';
import { DateTime } from 'luxon';
import { Component, createEffect, createSignal } from 'solid-js';

const commonColumnDefs: ColDef = {
  enableRowGroup: true,
  filter: 'agTextColumnFilter',
  cellStyle: agGridCellStyle,
  sortable: true,
  autoHeight: true,
};

const columnDefs: ColDef[] = [
  {
    ...commonColumnDefs,
    field: 'timeStamp',
    headerName: 'Timestamp',
    rowGroup: true,
    valueFormatter: (params: { value: string }) => {
      return params.value
        ? DateTime.fromISO(params.value).toFormat('yyyy-MM-dd HH:mm:ss a')
        : '';
    },
    minWidth: 210,
    hide: true,
    filter: 'agDateColumnFilter',
    filterParams: {
      comparator: dateComparator,
      buttons: ['reset'],
      suppressAndOrCondition: true,
      minValidYear: 1900,
      maxValidYear: new Date().getFullYear() + 1,
    },
    sort: 'asc',
  },
  {
    ...commonColumnDefs,
    field: 'action',
    headerName: 'Action',
  },
  {
    ...commonColumnDefs,
    field: 'field',
    headerName: 'Field',
  },
  {
    ...commonColumnDefs,
    field: 'oldValue',
    headerName: 'Old Value',
  },
  {
    ...commonColumnDefs,
    field: 'newValue',
    headerName: 'New Value',
  },
  {
    ...commonColumnDefs,
    field: 'editedBy',
    headerName: 'EditedBy',
  },
  {
    ...commonColumnDefs,
    field: 'description',
    headerName: 'Description',
    minWidth: 220,
  },
  {
    ...commonColumnDefs,
    field: 'historyType',
    headerName: 'HistoryType',
  },
];

const EditHistory: Component = () => {
  const params = useParams();
  const [editHistory, setEditHistory] = createSignal<CustomerEditHistoryItem[]>(
    [],
  );
  const [gridRef, setGridRef] = createSignal<AgGridSolidRef | null>(null);
  const fetchEditHistory = async () => {
    gridRef()?.api.showLoadingOverlay();
    const res = await fetchCustomerEditHistory(params.id as string);
    setEditHistory(res);
    gridRef()?.api.hideOverlay();
  };

  let previousId = params.id;
  createEffect(async () => {
    if (previousId !== params.id) {
      previousId = params.id;
      await fetchEditHistory();
    }
  });

  return (
    <div>
      <Notification
        type="warning"
        text="Edits may take up to 15 minutes to appear. Please wait for the table to update."
      />
      <Box class="ag-theme-alpine ag-theme-custom-border my-row-group-border custom-ag-row-icon-grid flex-1! h-[calc(100vh-255px)]">
        <AgGridSolid
          overlayNoRowsTemplate="No data available"
          overlayLoadingTemplate={atgLogo}
          ref={gridRef}
          columnDefs={columnDefs}
          rowData={editHistory()}
          getRowStyle={getRowGroupStyle}
          gridOptions={{
            groupDefaultExpanded: 1,
            autoGroupColumnDef: {
              width: 240,
            },
            pagination: true,
            groupDisplayType: 'multipleColumns',
            suppressPaginationPanel: false,
            rowGroupPanelShow: 'always',
            sideBar: {
              toolPanels: [columnsToolPanel, filtersToolPanel],
            },
            onGridReady: async (gridParams) => {
              setGridRef(gridParams);
              await fetchEditHistory();
            },
          }}
        />
      </Box>
    </div>
  );
};

export default EditHistory;
